import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

type AmbassadorStatus = 'pending_review' | 'approved' | 'rejected';

interface AmbassadorRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  website: string | null;
  agreed_terms: boolean;
  agreed_disclosure: boolean;
  status: AmbassadorStatus;
  admin_notes: string | null;
  created_at: string;
}

const STATUS_STYLES: Record<AmbassadorStatus, string> = {
  pending_review: 'bg-orange-10 text-orange-70',
  approved: 'bg-success-5 text-success-70',
  rejected: 'bg-destructive-5 text-red-600',
};

const STATUS_LABELS: Record<AmbassadorStatus, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AmbassadorRegistrations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<AmbassadorRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState<AmbassadorStatus>('pending_review');
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<AmbassadorStatus | 'all'>('all');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    checkAdminAndLoad();
  }, [user, navigate]);

  const checkAdminAndLoad = async () => {
    if (!user?.id) return;
    try {
      const isAdmin = await isUserAdmin(user.id);
      if (!isAdmin) { navigate('/login'); return; }
      await loadRegistrations();
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRegistrations = async () => {
    const { data, error } = await supabase
      .from('ambassador_registrations')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setRegistrations(data as unknown as AmbassadorRegistration[]);
  };

  const startEdit = (reg: AmbassadorRegistration) => {
    setEditingId(reg.id);
    setEditNotes(reg.admin_notes || '');
    setEditStatus(reg.status);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    const { error } = await supabase
      .from('ambassador_registrations')
      .update({ status: editStatus, admin_notes: editNotes || null, updated_at: new Date().toISOString() })
      .eq('id', editingId);
    if (!error) {
      setRegistrations((prev) =>
        prev.map((r) => r.id === editingId ? { ...r, status: editStatus, admin_notes: editNotes || null } : r)
      );
    }
    setSaving(false);
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const filtered = filterStatus === 'all'
    ? registrations
    : registrations.filter((r) => r.status === filterStatus);

  const counts = {
    pending_review: registrations.filter((r) => r.status === 'pending_review').length,
    approved: registrations.filter((r) => r.status === 'approved').length,
    rejected: registrations.filter((r) => r.status === 'rejected').length,
  };

  if (isLoading) return <LoadingSpinner message="Loading ambassador registrations..." />;

  return (
    <div className="min-h-screen bg-brand-5 font-urbanist">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="text-chocolate-400 hover:text-chocolate-700 text-sm transition-colors">
            &larr; Admin
          </Link>
          <span className="text-chocolate-300">/</span>
          <h1 className="text-text-xl text-chocolate-800">Ambassador Applications</h1>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {(Object.entries(counts) as [AmbassadorStatus, number][]).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
              className={`rounded-2xl border p-4 text-left transition-all ${
                filterStatus === status
                  ? 'border-chocolate-400 bg-white shadow-soft'
                  : 'border-chocolate-100 bg-white hover:border-chocolate-300'
              }`}
            >
              <p className="text-2xl font-bold text-chocolate-800 mb-0.5">{count}</p>
              <p className="text-xs font-semibold text-chocolate-400">{STATUS_LABELS[status]}</p>
            </button>
          ))}
        </div>

        {/* Filter label */}
        {filterStatus !== 'all' && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-chocolate-500">Filtering by:</span>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_STYLES[filterStatus]}`}>{STATUS_LABELS[filterStatus]}</span>
            <button onClick={() => setFilterStatus('all')} className="text-xs text-chocolate-400 hover:text-chocolate-700 underline">clear</button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-chocolate-100 bg-white p-12 text-center">
            <p className="text-chocolate-400">No applications {filterStatus !== 'all' ? `with status "${STATUS_LABELS[filterStatus]}"` : 'yet'}.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((reg) => (
              <motion.div
                key={reg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-chocolate-100 bg-white shadow-soft p-5"
              >
                {editingId === reg.id ? (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="font-semibold text-chocolate-800">{reg.first_name} {reg.last_name}</p>
                        <p className="text-sm text-chocolate-400">{reg.email}{reg.phone ? ` · ${reg.phone}` : ''}</p>
                        {reg.website && <p className="text-sm text-chocolate-400">{reg.website}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-chocolate-600 mb-1">Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value as AmbassadorStatus)}
                        className="w-full sm:w-48 px-3 py-2 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 focus:outline-none focus:ring-2 focus:ring-sage-400"
                      >
                        <option value="pending_review">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-chocolate-600 mb-1">Admin Notes</label>
                      <textarea
                        rows={3}
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes..."
                        className="w-full px-3 py-2 text-sm border border-chocolate-200 rounded-xl bg-cream-50 text-chocolate-800 placeholder-chocolate-300 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className="px-4 py-2 bg-chocolate-800 hover:bg-chocolate-900 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-cream-200 hover:bg-cream-300 text-chocolate-700 text-sm font-semibold rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-chocolate-800">{reg.first_name} {reg.last_name}</p>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[reg.status]}`}>
                          {STATUS_LABELS[reg.status]}
                        </span>
                      </div>
                      <p className="text-sm text-chocolate-500">
                        {reg.email}
                        {reg.phone ? <> &middot; {reg.phone}</> : null}
                      </p>
                      {reg.website && (
                        <p className="text-sm text-chocolate-400 mt-0.5">{reg.website}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-chocolate-400">
                        <span>Terms: {reg.agreed_terms ? 'Yes' : 'No'}</span>
                        <span>Disclosure: {reg.agreed_disclosure ? 'Yes' : 'No'}</span>
                      </div>
                      {reg.admin_notes && (
                        <p className="text-xs text-chocolate-400 mt-1.5 italic">{reg.admin_notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-chocolate-400">{formatDate(reg.created_at)}</span>
                      <button
                        onClick={() => startEdit(reg)}
                        className="px-3 py-1.5 bg-cream-200 hover:bg-cream-300 text-chocolate-700 text-xs font-semibold rounded-lg transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
